import { Link } from "react-router-dom";
import MarketingHeader from "@/components/layout/MarketingHeader";
import MarketingFooter from "@/components/layout/MarketingFooter";
import StatusBadge from "@/components/StatusBadge";
import {
  ArrowRight, Sparkles, CalendarDays, Coins, Share2, Zap, Repeat,
  Eye, LineChart, ShieldCheck, Layers, MessageSquare, Bell, Wallet,
  CheckCircle2, TrendingUp,
} from "lucide-react";

// Marketing homepage — full sales pitch for WhatsYourBalance.com.
// Sections (top → bottom): Hero → Trust bar → Three-Steps mockup →
// Feature grid (all major shipped capabilities) → Rich preview mockups
// (Year at a Glance, Pay Period, Snapshot share) → Who-it's-for →
// Objection-handling FAQ preview → Final CTA. Every mockup is inline
// HTML/CSS so the page never depends on external screenshot assets.
export default function Home() {
  return (
    <div className="min-h-screen" data-testid="marketing-home">
      <MarketingHeader />

      {/* ───────────────────────── HERO ───────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 md:px-8 pt-16 md:pt-24 pb-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 relative z-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#EEF3EC] text-[#3B5B3E] text-xs font-semibold uppercase tracking-widest px-3 py-1.5 mb-6">
              <Sparkles size={14} /> 1 month free · No card upfront
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#2C302E] leading-[1.05]">
              What&rsquo;s Your Balance?
            </h1>
            <p className="mt-6 text-lg text-[#59605C] leading-relaxed max-w-xl">
              Find money problems before they happen — no more stressing about the next two weeks or making it through the month.
            </p>
            <p className="mt-4 text-lg text-[#2C302E] leading-relaxed max-w-xl">
              <strong className="font-semibold">The answer:</strong> Follow the three steps below to look into the future and see how much money you&rsquo;ll have left over after you combine all of your bills and paydays. <Link to="/signup" className="text-[#4A6148] underline decoration-[#4A6148]/40 hover:decoration-[#4A6148] underline-offset-4 font-semibold">Sign up today for a FREE 30 Day Trial</Link>, no credit card required, no bank connection. Totally Risk-Free — you can even use our sample data to explore how it works.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/signup" data-testid="hero-cta-signup" className="btn-primary">
                Start your free month <ArrowRight size={16} />
              </Link>
              <Link to="/faq" data-testid="hero-cta-faq" className="btn-secondary">
                How it works
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm text-[#59605C]">
              <TrustItem icon={<CheckCircle2 size={14} />} label="No bank logins" />
              <TrustItem icon={<CheckCircle2 size={14} />} label="No spreadsheets" />
              <TrustItem icon={<CheckCircle2 size={14} />} label="No budget categories" />
              <TrustItem icon={<CheckCircle2 size={14} />} label="Cancel anytime" />
            </div>
          </div>
          {/* Hero preview — mimics the real Dashboard hero */}
          <div className="lg:col-span-5 relative">
            <DashboardMockup />
            <div className="absolute -z-10 -top-6 -right-6 w-40 h-40 rounded-full bg-[#DDE5DD] blur-2xl opacity-70" />
            <div className="absolute -z-10 bottom-0 -left-6 w-32 h-32 rounded-full bg-[#F3E1CF] blur-2xl opacity-70" />
          </div>
        </div>
      </section>

      {/* ─────────────────────── PROOF STRIP ─────────────────────── */}
      <section className="border-y border-[#EBE6E0] bg-white/60">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <ProofStat value="Weekly" label="Reconcile rhythm" />
          <ProofStat value="52-week" label="Forward forecast" />
          <ProofStat value="60 sec" label="To update balances" />
          <ProofStat value="$9.99/mo" label="After free month" />
        </div>
      </section>

      {/* ────────────────── PROBLEM → PROMISE ────────────────── */}
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-20">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D] mb-3">The problem</div>
          <h2
            className="font-heading text-3xl md:text-4xl font-medium text-[#2C302E] leading-tight"
            style={{ textWrap: "balance" }}
          >
            Budget apps ask you to categorize your life.{" "}
            <span className="text-[#4A6148] block sm:inline sm:before:content-['·_']">Bank apps just show today.</span>
          </h2>
          <p className="mt-6 text-[#59605C] text-lg leading-relaxed">
            Neither answers the one question that matters between paychecks: <em>&ldquo;Do I have enough right now to make it through?&rdquo;</em>
          </p>
          <p className="mt-4 text-[#2C302E] text-lg leading-relaxed">
            <span className="font-semibold">We answer that in three quiet steps</span> — no envelopes, no rules, no guilt. Just the truth about your paycheck and your bills.
          </p>
        </div>
      </section>

      {/* ─────────────────── FEATURE GRID ─────────────────── */}
      <section className="bg-white border-y border-[#EBE6E0]">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-20">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D] mb-3">Everything included</div>
            <h2
              className="font-heading text-3xl md:text-4xl font-medium text-[#2C302E]"
              style={{ textWrap: "balance" }}
            >
              Built for the way your money actually moves.
            </h2>
            <p className="mt-4 text-[#59605C]">Every feature earns its place. Nothing bloated, nothing you have to configure before you can see a number.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureCard
              icon={<CalendarDays size={20} />}
              title="Pay-period, 30-day, and month views"
              body="Toggle between &lsquo;this pay period&rsquo; (the two weeks that matter right now), &lsquo;next 30 days&rsquo; (which also opens a rolling 30 / 60 / 90-day strip so you can plan further ahead), and &lsquo;this month&rsquo; (the calendar picture)."
            />
            <FeatureCard
              icon={<TrendingUp size={20} />}
              title="Look 90 days ahead"
              body="Three rolling 30-day windows on the Dashboard — Next 30, Days 31–60, Days 61–90 — with the paychecks landing and the bills due in each. Every window's end-balance rolls forward so you know how each month sets up the next."
            />
            <FeatureCard
              icon={<Bell size={20} />}
              title="Next tight week alerts"
              body="A gentle heads-up when the closest tight or short week appears on the horizon — so you can act before you&rsquo;re staring at a red status."
            />
            <FeatureCard
              icon={<LineChart size={20} />}
              title="52-week Year At A Glance"
              body="A quiet heat-strip showing every safe, tight, and short week for the next year. Hover a week, see the breakdown. Click, pin it, plan."
            />
            <FeatureCard
              icon={<Share2 size={20} />}
              title="Share a week with your partner"
              body="One click turns any tight week into a public link — send it to a partner or roommate. They see the story, no login needed."
            />
            <FeatureCard
              icon={<Eye size={20} />}
              title="See who opened your share"
              body="When someone opens the link you sent, you get a heads-up email. Turns a share into a conversation instead of a message into the void."
            />
            <FeatureCard
              icon={<Repeat size={20} />}
              title="Recurring bills that just work"
              body="Set rent, phone, subscriptions once. Rolls forward every month automatically. Edit one occurrence or the whole series."
            />
            <FeatureCard
              icon={<Wallet size={20} />}
              title="All your funding accounts, one balance"
              body="Checking, savings, cash you carry, pay cards. Everything you can actually spend today, added up as one Real Balance."
            />
            <FeatureCard
              icon={<Zap size={20} />}
              title="Autopay-aware forecasting"
              body="Autopay bills show as pending until they charge, so your balance never lies to you about a bill that&rsquo;s about to hit."
            />
            <FeatureCard
              icon={<Layers size={20} />}
              title="Pending vs cleared, done right"
              body="Pending money you&rsquo;ve sent out reduces your usable cash right away. Expected income stays expected until you check it off."
            />
            <FeatureCard
              icon={<TrendingUp size={20} />}
              title="Weekly reconcile streaks"
              body="A gentle 60-second weekly check-in that builds a habit. See your streak grow. Miss a week, no shame — pick it back up."
            />
            <FeatureCard
              icon={<ShieldCheck size={20} />}
              title="You stay in the driver's seat"
              body="No bank logins. No credential sync. No third-party data sharing. You type your own balances so you&rsquo;re the only one who ever sees your numbers — the app answers to you, not to a bank aggregator."
            />
            <FeatureCard
              icon={<MessageSquare size={20} />}
              title="Plain-English status"
              body='No jargon, no charts to interpret. Just "Safe", "Tight", or "Short" for every pay period and month — and the reasoning behind each.'
            />
          </div>
        </div>
      </section>

      {/* ─────────────────── PREVIEW: YEAR VIEW ─────────────────── */}
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D] mb-3">Year At A Glance</div>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-[#2C302E] leading-tight">
              See every tight week — <span className="text-[#4A6148]">long before it arrives.</span>
            </h2>
            <p className="mt-5 text-[#59605C] leading-relaxed">
              52 weeks of forecasted balance in one calm strip. Green weeks are safe. Amber are tight (within your cushion). Red are short (below zero or your minimum). Perfect for spotting the month with the extra bill, the vacation crunch, or the paycheck gap.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-[#2C302E]">
              <BulletCheck>Roll it up by month for the executive view</BulletCheck>
              <BulletCheck>Click any week to pin its detail card</BulletCheck>
              <BulletCheck>Adjust your safety cushion so &ldquo;tight&rdquo; matches your comfort</BulletCheck>
            </ul>
          </div>
          <div className="lg:col-span-7">
            <YearHeatstripMockup />
          </div>
        </div>
      </section>

      {/* ─────────────────── PREVIEW: PAY PERIOD ─────────────────── */}
      <section className="bg-white border-y border-[#EBE6E0]">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 order-last lg:order-first">
            <PayPeriodMockup />
          </div>
          <div className="lg:col-span-5">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D] mb-3">Pay-period thinking</div>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-[#2C302E] leading-tight">
              Every bill under the <span className="text-[#4A6148]">paycheck that covers it.</span>
            </h2>
            <p className="mt-5 text-[#59605C] leading-relaxed">
              Weekly, biweekly, monthly, even 3-paycheck months — we group your bills under the paycheck that actually pays for them. See the money coming in, the bills going out, and what&rsquo;s left. For every pay period. Automatically.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-[#2C302E]">
              <BulletCheck>Click any paycheck to edit just that occurrence or the whole series</BulletCheck>
              <BulletCheck>Drill into &ldquo;bills due in this pay period&rdquo; without leaving the Dashboard</BulletCheck>
              <BulletCheck>Extra paycheck? We flag the month with ✨ so you can plan the win</BulletCheck>
            </ul>
          </div>
        </div>
      </section>

      {/* ─────────────────── PREVIEW: SHARE ─────────────────── */}
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D] mb-3">Snapshot sharing</div>
          <h2 className="font-heading text-3xl md:text-4xl font-medium text-[#2C302E] leading-tight">
            Bring your partner <span className="text-[#4A6148]">into the plan.</span>
          </h2>
          <p className="mt-5 text-[#59605C] leading-relaxed">
            Money conversations get easier when you&rsquo;re looking at the same picture. Turn any tight week into a public share link — no login required for the recipient — and you&rsquo;ll get an email the moment they open it.
          </p>
          <ul className="mt-6 space-y-3 text-sm text-[#2C302E]">
            <BulletCheck>Add a note (&ldquo;heads up — next week is tight, let&rsquo;s hold off on Uber Eats&rdquo;)</BulletCheck>
            <BulletCheck>Auto-expires after 30 days so nothing lingers</BulletCheck>
            <BulletCheck>View count on your Share button — see if the link actually landed</BulletCheck>
          </ul>
        </div>
        <div className="lg:col-span-7">
          <SnapshotShareMockup />
        </div>
      </section>

      {/* ─────────────────── HOW IT WORKS ─────────────────── */}
      <section className="bg-white border-y border-[#EBE6E0]">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-20 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D] mb-3">Three-step setup</div>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-[#2C302E]">
              You&rsquo;ll see your first forecast in under 5 minutes.
            </h2>
            <ol className="mt-8 space-y-6">
              <Step n="1" title="Add your spending accounts" body="Every bank account (checking, savings, pay cards) plus any cash you actually carry. Real Balance = the total of what you can spend today." />
              <Step n="2" title="Add your bills" body="Rent, utilities, subscriptions, credit-card minimums. Mark each as recurring (monthly/weekly/biweekly) so it rolls forward automatically." />
              <Step n="3" title="Add your income sources" body="Paychecks by frequency, plus one-time money like bonuses, tax refunds, or side-gig payouts. We project them forward for you." />
            </ol>
            <p className="mt-6 text-sm text-[#59605C]">Once those three are in, the Dashboard tells you a single number: <span className="font-semibold text-[#2C302E]">how much is left after every bill is paid.</span></p>
          </div>
          <ThreeStepMockup />
        </div>
      </section>

      {/* ─────────────────── WHO IT'S FOR ─────────────────── */}
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D] mb-3">Who it&rsquo;s for</div>
          <h2 className="font-heading text-3xl md:text-4xl font-medium text-[#2C302E]">If you&rsquo;ve ever stared at your bank balance and asked&hellip;</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          <QuoteCard quote="Will I make it to Friday?" answer="Yes — you have $210 left after the two bills due this week." />
          <QuoteCard quote="Is this an extra paycheck month?" answer="Yep — this month you get three. We flag it with ✨." />
          <QuoteCard quote="Can we afford that trip next month?" answer="Not quite — you'd finish the month at -$85. Push it two weeks and you're at +$540." />
        </div>
      </section>

      {/* ─────────────────── FAQ TEASER ─────────────────── */}
      <section className="bg-white border-y border-[#EBE6E0]">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-20">
          <div className="text-center mb-10">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D] mb-3">Common questions</div>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-[#2C302E]">Objections we hear (and answer)</h2>
          </div>
          <div className="space-y-4">
            <FaqPreview
              q="Do I have to connect my bank?"
              a="No. You type in your balances. That's the point — you stay in control of what the app sees, and there's zero credential risk."
            />
            <FaqPreview
              q="Is this another budgeting app?"
              a="No. Budgets assign every dollar a job. We forecast what your paycheck covers. No categories, no envelopes, no rules to follow."
            />
            <FaqPreview
              q="What if I miss a week?"
              a="Nothing breaks. Your streak resets, but the forecast keeps working. Just reconcile when you're ready."
            />
            <FaqPreview
              q="Can I cancel anytime?"
              a="Yes. One click. No emails, no retention forms."
            />
          </div>
          <div className="mt-8 text-center">
            <Link to="/faq" className="btn-secondary" data-testid="home-faq-link">See all questions →</Link>
          </div>
        </div>
      </section>

      {/* ─────────────────── FINAL CTA ─────────────────── */}
      <section className="max-w-4xl mx-auto px-6 md:px-8 py-24 text-center">
        <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D] mb-3">Try it free</div>
        <h2 className="font-heading text-3xl md:text-5xl font-medium text-[#2C302E] leading-tight">
          Stop guessing whether your paycheck will cover the month.
        </h2>
        <p className="mt-5 text-[#59605C] text-lg max-w-xl mx-auto">
          Free for a month, no card required. $9.99/month after that if you love it. Cancel anytime.
        </p>
        <div className="mt-8 flex justify-center gap-3 flex-wrap">
          <Link to="/signup" className="btn-primary" data-testid="home-signup-cta-final">
            Start your free month <ArrowRight size={16} />
          </Link>
          <Link to="/pricing" className="btn-secondary" data-testid="home-pricing-link">See pricing</Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

// ─────────────────────────── Bits & atoms ───────────────────────────
function TrustItem({ icon, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[#4A6148]">{icon}</span>
      <span>{label}</span>
    </div>
  );
}

function ProofStat({ value, label }) {
  return (
    <div>
      <div className="font-heading text-2xl md:text-3xl font-medium text-[#2C302E]">{value}</div>
      <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D] mt-1">{label}</div>
    </div>
  );
}

function FeatureCard({ icon, title, body }) {
  // Feature cards live on a plain white section — the default `.card-soft`
  // treatment reads too soft here, so this variant swaps to a warm cream
  // fill + darker warm-tan border for real card presence. Introduced Feb
  // 2026 after the site-wide contrast pass flagged this grid as still
  // under-defined against the white parent.
  return (
    <div className="p-6 rounded-3xl bg-[#FBF6EA] border-2 border-[#B8A882] shadow-[0_2px_0_#B8A88240] hover:shadow-[0_6px_16px_rgba(44,48,46,0.08)] hover:-translate-y-0.5 transition-all">
      <div className="w-10 h-10 rounded-full bg-[#EEF3EC] text-[#4A6148] flex items-center justify-center mb-3 ring-1 ring-[#B8A882]/40">{icon}</div>
      <div className="font-heading text-base font-semibold text-[#2C302E]">{title}</div>
      <div className="mt-2 text-sm text-[#59605C] leading-relaxed">{body}</div>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <li className="flex gap-4">
      <div className="w-9 h-9 rounded-full bg-[#EEF3EC] text-[#3B5B3E] flex items-center justify-center font-heading font-semibold shrink-0">{n}</div>
      <div>
        <div className="font-heading font-medium text-[#2C302E]">{title}</div>
        <div className="text-[#59605C] mt-1 text-sm leading-relaxed">{body}</div>
      </div>
    </li>
  );
}

function BulletCheck({ children }) {
  return (
    <li className="flex items-start gap-2.5">
      <CheckCircle2 size={16} className="text-[#4A6148] mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function QuoteCard({ quote, answer }) {
  return (
    <div className="card-soft p-6">
      <div className="text-[#8A918D] text-xs uppercase tracking-widest font-semibold mb-2">You wondered</div>
      <div className="font-heading text-lg text-[#2C302E] italic leading-snug">&ldquo;{quote}&rdquo;</div>
      <div className="mt-4 pt-4 border-t border-[#EBE6E0]">
        <div className="text-[#8A918D] text-xs uppercase tracking-widest font-semibold mb-1">We answer</div>
        <div className="text-sm text-[#2C302E] leading-relaxed">{answer}</div>
      </div>
    </div>
  );
}

function FaqPreview({ q, a }) {
  return (
    <details className="card-soft p-5 group cursor-pointer">
      <summary className="font-medium text-[#2C302E] list-none flex items-center justify-between gap-3">
        <span>{q}</span>
        <span className="text-[#4A6148] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
      </summary>
      <p className="mt-3 text-sm text-[#59605C] leading-relaxed">{a}</p>
    </details>
  );
}

// ─────────────────────────── Mockup components ───────────────────────────
function DashboardMockup() {
  return (
    <div className="card-soft p-6 md:p-7" data-testid="hero-preview-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#8A918D]">This pay period</div>
          <div className="font-heading text-xl text-[#2C302E] mt-0.5">Aug 12 → Aug 25</div>
        </div>
        <StatusBadge status="tight" testId="hero-status" />
      </div>
      <div className="rounded-2xl bg-[#EEF3EC] border border-[#B8CFB1] px-4 py-3 flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-[#3B5B3E]">Step 1 · Bank &amp; Cash</div>
          <div className="text-xs text-[#59605C]">Real balance after pending clears</div>
        </div>
        <div className="font-heading font-semibold text-[#2C302E]">$1,485</div>
      </div>
      <div className="rounded-2xl bg-[#FFF9EA] border border-[#E9D69F] px-4 py-3 flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-[#7A4E1F]">Step 2 · Unpaid Bills</div>
          <div className="text-xs text-[#59605C]">Bills due in this pay period</div>
        </div>
        <div className="font-heading font-semibold text-[#8C3A30]">-$1,232</div>
      </div>
      <div className="rounded-2xl bg-[#EEF3EC] border border-[#B8CFB1] px-4 py-3 flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-[#3B5B3E]">Step 3 · Money Coming In</div>
          <div className="text-xs text-[#59605C]">Paychecks still expected</div>
        </div>
        <div className="font-heading font-semibold text-[#3B5B3E]">+$2,717</div>
      </div>
      <div className="rounded-2xl bg-[#F9F8F6] border border-[#D8CFB9] px-4 py-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">Remaining balance</div>
          <div className="text-xs text-[#59605C]">After every bill is paid</div>
        </div>
        <div className="font-heading text-2xl font-semibold text-[#3B5B3E]">$2,970</div>
      </div>
    </div>
  );
}

function YearHeatstripMockup() {
  // Realistic 52-week pattern — mostly safe/green with clusters of tight/short
  // that mirror what a real user's Year At A Glance would look like.
  const weeks = [
    "s", "s", "s", "t", "s", "s", "s", "s", "s", "t", "t", "s", "s",
    "s", "s", "s", "s", "s", "s", "S", "s", "s", "s", "t", "s", "s",
    "s", "s", "s", "s", "s", "s", "s", "s", "S", "s", "s", "s", "s",
    "s", "t", "t", "s", "s", "s", "s", "s", "s", "s", "S", "s", "s",
  ];
  const color = (c) => c === "S" ? "bg-[#8C3A30]" : c === "t" ? "bg-[#B48A24]" : "bg-[#4A6148]";
  const height = (c) => c === "S" ? "h-14" : c === "t" ? "h-10" : "h-8";
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return (
    <div className="card-soft p-6 md:p-8" data-testid="year-heatstrip-mockup">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D]">Year at a glance</div>
          <div className="font-heading text-xl text-[#2C302E]">52 weeks · pinned week 20</div>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-widest">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#4A6148]" /> Safe</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#B48A24]" /> Tight</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#8C3A30]" /> Short</span>
        </div>
      </div>
      <div className="flex justify-between text-[9px] font-semibold uppercase tracking-widest text-[#8A918D] mb-1 px-1">
        {months.map((m) => <span key={m}>{m}</span>)}
      </div>
      <div className="flex items-end gap-[3px] h-16">
        {weeks.map((c, i) => (
          <div key={i} className={`flex-1 ${color(c)} ${height(c)} rounded-t-sm ${c === "S" ? "ring-2 ring-[#8C3A30]/40" : ""}`} />
        ))}
      </div>
      <div className="mt-4 rounded-xl bg-[#FEF3EE] border border-[#F0C8B8] p-3 text-xs text-[#7A2E24]">
        <span className="font-semibold">Week 20 — Short.</span> Projected end-balance -$85. 5 bills due. Click to see the breakdown.
      </div>
    </div>
  );
}

function PayPeriodMockup() {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <PayPeriodCard status="safe" range="Aug 12 → Aug 25" pay="Aug 12" income={2717} bills={1232} left={1485} highlight />
      <PayPeriodCard status="short" range="Aug 26 → Sep 8" pay="Aug 26" income={3661} bills={5278} left={-1617} />
      <PayPeriodCard status="safe" range="Sep 9 → Sep 22" pay="Sep 9" income={2717} bills={1075} left={1642} />
      <PayPeriodCard status="safe" range="Sep 23 → Oct 6" pay="Sep 23" income={2717} bills={890} left={1827} extra />
    </div>
  );
}

function PayPeriodCard({ status, range, pay, income, bills, left, highlight, extra }) {
  const pill = status === "short" ? "bg-[#FEF3EE] text-[#7A2E24] border-[#F0C8B8]" : "bg-[#EEF3EC] text-[#3B5B3E] border-[#B8CFB1]";
  const leftColor = left < 0 ? "text-[#8C3A30]" : "text-[#3B5B3E]";
  return (
    <div className={`rounded-2xl border p-4 ${highlight ? "bg-white border-[#4A6148] shadow-sm" : "bg-[#F9F8F6] border-[#EBE6E0]"}`}>
      <div className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border ${pill}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${status === "short" ? "bg-[#8C3A30]" : "bg-[#3B5B3E]"}`} />
        {status}
      </div>
      <div className="mt-2 text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">Pay period</div>
      <div className="font-heading text-sm text-[#2C302E] leading-snug">{range}</div>
      <div className="text-[10px] text-[#8A918D] mt-0.5">Paycheck arrives {pay} {extra && "· ✨ extra paycheck"}</div>
      <div className="mt-3 space-y-0.5 text-xs">
        <div className="flex justify-between text-[#59605C]"><span>Income</span><span className="text-[#3B5B3E]">+${income.toLocaleString()}</span></div>
        <div className="flex justify-between text-[#59605C]"><span>Bills</span><span className="text-[#8C3A30]">-${bills.toLocaleString()}</span></div>
      </div>
      <div className="mt-2 pt-2 border-t border-[#EBE6E0] flex justify-between text-xs font-semibold">
        <span className="text-[#2C302E]">Estimated balance</span>
        <span className={leftColor}>{left < 0 ? "-" : ""}${Math.abs(left).toLocaleString()}</span>
      </div>
    </div>
  );
}

function SnapshotShareMockup() {
  return (
    <div className="card-soft p-6 md:p-8 relative" data-testid="snapshot-share-mockup">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">Shared by Alex</div>
      <div className="font-heading text-lg text-[#2C302E] mt-1">Heads up — a bumpy week is coming</div>
      <div className="mt-4 rounded-2xl bg-[#FEF3EE] border border-[#F0C8B8] p-4">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#8C3A30]" />
          <span className="text-[10px] font-semibold uppercase tracking-widest text-[#7A2E24]">Short week ahead</span>
        </div>
        <div className="font-heading text-lg font-semibold text-[#7A2E24] mt-1">Aug 24 → Aug 30</div>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="rounded-xl bg-white/70 p-3">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">End balance</div>
            <div className="font-heading font-semibold text-[#8C3A30] text-lg">-$42.50</div>
          </div>
          <div className="rounded-xl bg-white/70 p-3">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">Bills due</div>
            <div className="font-heading font-semibold text-[#2C302E] text-lg">2</div>
          </div>
        </div>
        <div className="mt-3 rounded-lg bg-white/70 border border-[#D8CFB9] p-3 text-xs text-[#2C302E]">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D] mb-1">Note</div>
          Hey — Sunday looks rough. Let&rsquo;s hold off on the streaming subscription and skip take-out this week?
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between text-[11px] text-[#8A918D]">
        <span>Public link · no login needed</span>
        <span className="inline-flex items-center gap-1 font-semibold text-[#4A6148]"><Eye size={12} /> 3 views</span>
      </div>
    </div>
  );
}

function ThreeStepMockup() {
  return (
    <div className="card-soft p-6 space-y-3" data-testid="three-step-mockup">
      <div className="text-xs font-semibold uppercase tracking-wider text-[#8A918D]">Bills due before next paycheck</div>
      {[
        { name: "Water Bill", date: "Today", amount: "$60", status: "unpaid" },
        { name: "Credit Card Minimum", date: "in 1 day", amount: "$95", status: "unpaid" },
        { name: "Phone (autopay)", date: "in 2 days", amount: "$85", status: "pending" },
        { name: "Streaming", date: "in 5 days", amount: "$16", status: "paid" },
      ].map((b) => <BillRow key={b.name} {...b} />)}
      <div className="pt-3 border-t border-[#EBE6E0] flex items-center justify-between text-sm">
        <span className="text-[#59605C]">Total due before paycheck</span>
        <span className="font-heading font-semibold text-[#2C302E]">$256</span>
      </div>
    </div>
  );
}

function BillRow({ name, date, amount, status }) {
  const map = {
    unpaid: "bg-[#F2EFEB] text-[#59605C]",
    pending: "status-tight",
    paid: "status-safe",
  };
  return (
    <div className="flex items-center justify-between p-3 rounded-xl bg-[#F9F8F6]">
      <div>
        <div className="font-medium text-[#2C302E] text-sm">{name}</div>
        <div className="text-xs text-[#8A918D]">{date}</div>
      </div>
      <div className="flex items-center gap-3">
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${map[status]}`}>{status}</span>
        <span className="font-semibold text-[#2C302E] text-sm">{amount}</span>
      </div>
    </div>
  );
}
